import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { View, StyleSheet } from "react-native";
import { useSetMessageFeedbackMutation, useGetLearningTtsTokenMutation } from "../../../graphql/generated/graphql";
import { type MessageItem } from "../../companion/MessagesContainer";
import { useAITutorAudioPlayback } from "../../../hooks/useAITutorAudioPlayback";
import { useLearningTtsWebSocket } from "../../../hooks/useLearningTtsWebSocket";
import type { StreamAudioChunkPayload } from "../../../hooks/useLearningLlmStream";
import type { ComposerAttachment } from "../../../components/chatNewInterface/chatLayout/messageComposer/ChatComposerBar";
import AITutorMessagesContainer from "./AITutorMessagesContainer";
import AITutorChatBarContainer from "../../navigation/AITutorChatBarContainer/AITutorChatBarContainer";
import CompanionErrorModal from "../../../components/companion/CompanionErrorModal/CompanionErrorModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import LottieView from "lottie-react-native";
import LottieUrl from "../../../assets/lottie/aiTutorChatLoadingLottie.json";
import { MathVirtualKeyboardProvider } from "../../../contexts/MathVirtualKeyboardContext";
import { shadowStyles } from "../../../styles/shadow";
import { radii, spacing, semanticColors } from "../../../styles/design-system";

const INTRO_TEXTS = [
  "Analyzing your progress and tailoring the conversation...",
  "The more you lean into learning, the faster you'll progress through your objectives.",
  "We detect when answers are copied from AI or other sources - please be original in your responses.",
  "Ask questions when you're curious! The AI loves when you dig deeper into topics.",
  "Don't worry about making mistakes - they're part of the learning process and help you grow.",
  "Take your time to think through each question. Quality of thought matters more than speed.",
  "Your previous conversations help the AI understand your learning style - each session gets more personalized.",
  "Try explaining concepts in your own words - it helps solidify your understanding.",
  "The AI remembers where you've struggled before and will help you strengthen those areas.",
  "Engage authentically - we can tell when responses aren't genuinely your own thinking.",
  "Every question is an opportunity to deepen your understanding of the subject.",
  "Your learning journey is unique - no two students will have the same experience here.",
  "Think critically about each concept before responding - deeper thinking leads to better learning.",
  "Feel free to ask for clarification if something doesn't make sense.",
  "The more engaged you are in the conversation, the more the AI can help you learn.",
  "Building on your past conversations to create the perfect learning experience for you.",
  "Quality responses help the AI understand your thought process and adapt accordingly.",
  "Each interaction helps build a more personalized and effective learning path.",
  "Curiosity and genuine engagement are the keys to accelerating your progress.",
  "Preparing an adaptive conversation based on your unique learning patterns...",
];

const getNextIntroText = (currentText?: string) => {
  if (INTRO_TEXTS.length <= 1) {
    return INTRO_TEXTS[0] ?? "";
  }
  const availableTexts = INTRO_TEXTS.filter((text) => text !== currentText);
  return availableTexts[Math.floor(Math.random() * availableTexts.length)] ?? INTRO_TEXTS[0];
};

type ChatDataMessage = {
  id?: string;
  content?: string;
  sender?: string;
  created?: string;
  files?: Array<{ id: string; fileName: string }>;
  todos?: Array<{ id: string; name: string; date?: string | null }>;
  assignments?: Array<{ id: string; name?: string | null; due_date?: string | null }>;
};

type ChatData = {
  id?: string;
  conversation?: ChatDataMessage[];
};

interface AITutorChatContainerProps {
  chatData: ChatData | undefined;
  chatLoading: boolean;
  resourceId: string;
  isScreenFocused?: boolean;
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamingStateChange?: (isStreaming: boolean) => void;
  onStreamCompleted?: () => void;
}

function AITutorChatContainer({
  chatData,
  chatLoading,
  resourceId,
  isScreenFocused = true,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamingStateChange,
  onStreamCompleted,
}: AITutorChatContainerProps) {
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);
  const streamingMessageIdRef = useRef<string | null>(null);
  const hasStreamedLocallyRef = useRef(false);

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorRetry, setErrorRetry] = useState<(() => void) | undefined>(undefined);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState<Record<string, "up" | "down">>({});
  const [setMessageFeedbackMutation] = useSetMessageFeedbackMutation();
  const [getLearningTtsToken] = useGetLearningTtsTokenMutation();
  const [readingAloudMessageId, setReadingAloudMessageId] = useState<string | null>(null);
  const [isReadingAloudPaused, setIsReadingAloudPaused] = useState(false);
  const [isFirstAiStreamInProgress, setIsFirstAiStreamInProgress] = useState(false);
  const [shouldRevealFirstMessageAtTop, setShouldRevealFirstMessageAtTop] = useState(false);
  const [introText, setIntroText] = useState(() => INTRO_TEXTS[0] ?? "");
  const isFirstAiStreamInProgressRef = useRef(false);
  const hasCompletedFirstAiStreamRef = useRef(false);
  const shouldDelayFirstIntroAudioRef = useRef(false);
  const pendingFirstIntroAudioChunksRef = useRef<StreamAudioChunkPayload[]>([]);

  const { resetAudioPlayback, handleAudioStreamChunk, notifyStreamStarted, notifyStreamCompleted } =
    useAITutorAudioPlayback(isAudioEnabled, resourceId);

  const getReadAloudToken = useCallback(async (): Promise<string | null> => {
    try {
      const result = await getLearningTtsToken();
      return result.data?.getLearningTtsToken?.token ?? null;
    } catch {
      return null;
    }
  }, [getLearningTtsToken]);

  const readAloudTtsWs = useLearningTtsWebSocket({
    onAudioChunk: handleAudioStreamChunk,
    getToken: getReadAloudToken,
    onStreamEnded: () => {
      setReadingAloudMessageId(null);
      setIsReadingAloudPaused(false);
    },
  });

  const shouldShowIntroLoader = !chatData?.id && isFirstAiStreamInProgress && currentStreamingMessageId !== null;

  const initialMessage = useMemo(
    () => (chatData?.id ? "Let's continue where we left off" : "Please begin by introducing me to this topic"),
    [chatData?.id]
  );

  useEffect(() => {
    hasStreamedLocallyRef.current = false;
    streamingMessageIdRef.current = null;
    isFirstAiStreamInProgressRef.current = false;
    hasCompletedFirstAiStreamRef.current = false;
    shouldDelayFirstIntroAudioRef.current = false;
    pendingFirstIntroAudioChunksRef.current = [];
    setCurrentStreamingMessageId(null);
    setIsFirstAiStreamInProgress(false);
    setShouldRevealFirstMessageAtTop(false);
    setMessages([]);
    setIntroText(INTRO_TEXTS[0] ?? "");
    setReadingAloudMessageId(null);
    setIsReadingAloudPaused(false);
    readAloudTtsWs.disconnect();
  }, [resourceId]);

  useEffect(() => {
    if (chatData?.id) {
      hasCompletedFirstAiStreamRef.current = true;
      isFirstAiStreamInProgressRef.current = false;
      setIsFirstAiStreamInProgress(false);
    }
  }, [chatData?.id]);

  useEffect(() => {
    onStreamingStateChange?.(currentStreamingMessageId !== null);
  }, [currentStreamingMessageId, onStreamingStateChange]);

  useEffect(() => {
    if (!shouldShowIntroLoader) {
      return;
    }
    setIntroText((prev) => getNextIntroText(prev));
    const intervalId = setInterval(() => {
      setIntroText((prev) => getNextIntroText(prev));
    }, 2500);
    return () => {
      clearInterval(intervalId);
    };
  }, [shouldShowIntroLoader]);

  useEffect(() => {
    if (!chatData?.conversation || hasStreamedLocallyRef.current) {
      return;
    }

    const serverMessages: MessageItem[] = [...chatData.conversation].reverse().map((message, index) => ({
      id: message.id ?? `server-message-${index}`,
      sender: message.sender ?? "ai",
      content: message.content ?? "",
      created: message.created,
      files: message.files?.length ? message.files.map((f) => ({ id: f.id, fileName: f.fileName })) : undefined,
      todos: message.todos?.length ? message.todos : undefined,
      assignments: message.assignments?.length
        ? message.assignments.map((a) => ({ id: a.id, name: a.name ?? undefined, due_date: a.due_date ?? undefined }))
        : undefined,
    }));

    setMessages(serverMessages);

    const initialFeedback: Record<string, "up" | "down"> = {};
    for (const message of chatData.conversation) {
      const fb = (message as any).feedback;
      if (fb === "up" || fb === "down") {
        initialFeedback[message.id ?? ""] = fb;
      }
    }
    setFeedbackByMessageId(initialFeedback);
  }, [chatData]);

  const handleStreamStart = useCallback(
    (message: string, attachment?: ComposerAttachment | null, meta?: { isAutoStart?: boolean }) => {
      hasStreamedLocallyRef.current = true;
      const isFirstAiStream = Boolean(meta?.isAutoStart) && !chatData?.id && !hasCompletedFirstAiStreamRef.current;
      if (isFirstAiStream) {
        isFirstAiStreamInProgressRef.current = true;
        setIsFirstAiStreamInProgress(true);
        setShouldRevealFirstMessageAtTop(false);
        shouldDelayFirstIntroAudioRef.current = true;
        pendingFirstIntroAudioChunksRef.current = [];
      } else {
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        hasCompletedFirstAiStreamRef.current = true;
        shouldDelayFirstIntroAudioRef.current = false;
        pendingFirstIntroAudioChunksRef.current = [];
      }
      setResetAutoScroll((prev) => prev + 1);
      const userMessageId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const aiTempId = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      streamingMessageIdRef.current = aiTempId;
      setCurrentStreamingMessageId(aiTempId);
      const files = attachment ? [{ id: attachment.id, fileName: attachment.name }] : undefined;
      void resetAudioPlayback();
      notifyStreamStarted();

      setMessages((prev) => {
        const nextMessages = [...prev];
        if (!meta?.isAutoStart) {
          nextMessages.push({ id: userMessageId, sender: "human", content: message, files });
        }
        nextMessages.push({ id: aiTempId, sender: "ai", content: "" });
        return nextMessages;
      });
    },
    [chatData?.id, notifyStreamStarted, resetAudioPlayback]
  );

  const handleStreamChunk = useCallback(({ text, isFinalChunk }: { text: string; isFinalChunk?: boolean }) => {
    const streamingId = streamingMessageIdRef.current;
    if (!streamingId) {
      return;
    }
    if (text) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === streamingId ? { ...message, content: `${message.content ?? ""}${text}` } : message
        )
      );
    }
    if (isFinalChunk) {
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
    }
  }, []);

  const handleStreamComplete = useCallback(() => {
    if (isFirstAiStreamInProgressRef.current) {
      hasCompletedFirstAiStreamRef.current = true;
      isFirstAiStreamInProgressRef.current = false;
      setIsFirstAiStreamInProgress(false);
      setShouldRevealFirstMessageAtTop(true);
    }
    streamingMessageIdRef.current = null;
    setCurrentStreamingMessageId(null);
    notifyStreamCompleted();
    onStreamCompleted?.();
  }, [notifyStreamCompleted, onStreamCompleted]);

  const handleRevealedFirstMessageAtTop = useCallback(() => {
    setShouldRevealFirstMessageAtTop(false);
  }, []);

  const handleIncomingAudioChunk = useCallback(
    (payload: StreamAudioChunkPayload) => {
      if (shouldDelayFirstIntroAudioRef.current && isFirstAiStreamInProgressRef.current) {
        const alreadyQueued = pendingFirstIntroAudioChunksRef.current.some((chunk) => chunk.seq === payload.seq);
        if (!alreadyQueued) {
          pendingFirstIntroAudioChunksRef.current.push(payload);
        }
        return;
      }
      handleAudioStreamChunk(payload);
    },
    [handleAudioStreamChunk]
  );

  useEffect(() => {
    if (shouldShowIntroLoader || !shouldDelayFirstIntroAudioRef.current) {
      return;
    }

    const queuedChunks = [...pendingFirstIntroAudioChunksRef.current].sort((a, b) => a.seq - b.seq);
    pendingFirstIntroAudioChunksRef.current = [];
    shouldDelayFirstIntroAudioRef.current = false;

    queuedChunks.forEach((chunk) => {
      handleAudioStreamChunk(chunk);
    });
  }, [handleAudioStreamChunk, shouldShowIntroLoader]);

  const handleStreamError = useCallback(
    ({ title, message, onRetry }: { title: string; message: string; onRetry?: () => void }) => {
      const streamingId = streamingMessageIdRef.current;
      if (streamingId) {
        setMessages((prev) => prev.filter((item) => item.id !== streamingId));
      }
      if (isFirstAiStreamInProgressRef.current) {
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        setShouldRevealFirstMessageAtTop(false);
      }
      shouldDelayFirstIntroAudioRef.current = false;
      pendingFirstIntroAudioChunksRef.current = [];
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
      setErrorTitle(title);
      setErrorMessage(message);
      setErrorRetry(() => onRetry);
      setErrorModalVisible(true);
      void resetAudioPlayback();
    },
    [resetAudioPlayback]
  );

  const handleFeedback = useCallback(
    async (messageId: string, value: "up" | "down" | null) => {
      setFeedbackByMessageId((prev) => {
        if (value === null) {
          const next = { ...prev };
          delete next[messageId];
          return next;
        }
        return { ...prev, [messageId]: value };
      });
      datadogRum.addAction("message_feedback", { messageId, chatType: "ile", value });
      try {
        await setMessageFeedbackMutation({ variables: { messageId, value: value ?? "" } });
      } catch {
        setErrorTitle("Feedback Error");
        setErrorMessage("Unable to submit feedback. Please try again.");
        setErrorModalVisible(true);
      }
    },
    [setMessageFeedbackMutation]
  );

  const handleReadAloud = useCallback(
    async (messageId: string, content: string) => {
      if (readingAloudMessageId === messageId) {
        readAloudTtsWs.disconnect();
        await resetAudioPlayback();
        setReadingAloudMessageId(null);
        setIsReadingAloudPaused(false);
        return;
      }
      // Resume AudioContext now — must be within the user-gesture call stack, before any awaits.
      notifyStreamStarted();
      if (readingAloudMessageId) {
        readAloudTtsWs.disconnect();
      }
      // Always reset so the seq deduplication counter starts from 0 for the new stream.
      await resetAudioPlayback();
      setReadingAloudMessageId(messageId);
      setIsReadingAloudPaused(false);
      readAloudTtsWs.connect();
      readAloudTtsWs.sendSegmentText(0, content);
    },
    [readingAloudMessageId, readAloudTtsWs, resetAudioPlayback, notifyStreamStarted]
  );

  const handlePauseReadAloud = useCallback(() => {
    setIsReadingAloudPaused(true);
  }, []);

  const handleResumeReadAloud = useCallback(() => {
    setIsReadingAloudPaused(false);
  }, []);

  const handleAudioToggleChange = useCallback(
    (enabled: boolean) => {
      setIsAudioEnabled(enabled);
      if (!enabled) {
        readAloudTtsWs.disconnect();
        void resetAudioPlayback();
        setReadingAloudMessageId(null);
        setIsReadingAloudPaused(false);
      }
    },
    [readAloudTtsWs, resetAudioPlayback]
  );

  useEffect(() => {
    return () => {
      void resetAudioPlayback();
    };
  }, [resetAudioPlayback]);

  useEffect(() => {
    if (!isScreenFocused) {
      void resetAudioPlayback();
    }
  }, [isScreenFocused, resetAudioPlayback]);

  return (
    <MathVirtualKeyboardProvider>
      <View style={styles.container}>
        <View style={styles.content}>
          <View style={styles.messagesArea}>
            {shouldShowIntroLoader ? (
              <View style={styles.loaderWrapper}>
                <View style={[styles.loaderContainer, shadowStyles.allAroundShadow]}>
                  <LottieView autoPlay source={LottieUrl} speed={1} loop={true} style={{ width: 100, height: 100 }} />
                  <CustomText
                    text="Loading your personalized learning experience"
                    useTranslationText={false}
                    size="l"
                    weight="bold"
                    style={{ textAlign: "center" }}
                    aria-level="2"
                    role="heading"
                  />
                  <CustomText
                    text={introText}
                    useTranslationText={false}
                    size="m"
                    weight="regular"
                    style={{ marginTop: 10, textAlign: "center" }}
                  />
                </View>
              </View>
            ) : (
              <AITutorMessagesContainer
                messages={messages}
                isStreaming={currentStreamingMessageId !== null}
                currentStreamingMessageId={currentStreamingMessageId}
                resetAutoScroll={resetAutoScroll}
                shouldRevealFirstMessageAtTop={shouldRevealFirstMessageAtTop}
                onRevealedFirstMessageAtTop={handleRevealedFirstMessageAtTop}
                feedbackByMessageId={feedbackByMessageId}
                onFeedback={handleFeedback}
                readingAloudMessageId={readingAloudMessageId}
                isReadingAloudPaused={isReadingAloudPaused}
                onReadAloud={handleReadAloud}
                onPauseReadAloud={handlePauseReadAloud}
                onResumeReadAloud={handleResumeReadAloud}
              />
            )}
          </View>
          <View style={styles.chatInputWrapper}>
            <AITutorChatBarContainer
              key={resourceId}
              chatId={chatData?.id ?? null}
              contentNodeId={resourceId}
              autoStart={!chatLoading && !chatData?.id}
              initialMessage={initialMessage}
              onStreamStart={handleStreamStart}
              onStreamChunk={handleStreamChunk}
              onAudioStreamChunk={handleIncomingAudioChunk}
              onAudioStreamStarted={notifyStreamStarted}
              onAudioStreamEnded={notifyStreamCompleted}
              onAudioToggleChange={handleAudioToggleChange}
              onObjectiveCompleted={onObjectiveCompleted}
              onAllObjectivesCompleted={onAllObjectivesCompleted}
              onStreamComplete={handleStreamComplete}
              onStreamError={handleStreamError}
            />
          </View>
        </View>
        <CompanionErrorModal
          visible={errorModalVisible}
          title={errorTitle}
          message={errorMessage}
          onRetry={errorRetry}
          onClose={() => {
            setErrorModalVisible(false);
            setErrorRetry(undefined);
          }}
        />
      </View>
    </MathVirtualKeyboardProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.white,
  },
  content: {
    flex: 1,
    position: "relative",
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.light,
  },
  chatInputWrapper: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
    zIndex: 900,
    marginTop: spacing.sm,
  },
  loaderWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loaderContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: semanticColors.surface.white,
    borderRadius: radii.lg,
    borderWidth: 0,
    maxWidth: 500,
    width: "90%",
    padding: spacing["2xl"],
  },
});

export default AITutorChatContainer;
